import * as React from "react";
import { createEntityComponent } from "@coltorapps/builder-react";
import { Label } from "@/components/ui/label";
import { formatError, ValidationError } from "@/components/ui/validation-error";
import { useFieldValidationState } from "@/hooks/useFieldValidationState";

import { DataGrid, type GridHeaderItem } from "@ui/DataGrid";
import { useGridPreview } from "@ui/DataGrid/grid-preview-context";
import { gridEntity } from "./definition";
import { parseGridRows } from "./grid-value-validation";

export const GridEntity = createEntityComponent(
  gridEntity,
  function GridEntity(props) {
    const id = React.useId();
    const showHiddenColumnsPreview = useGridPreview();
    const readyForValidationRef = React.useRef(false);
    const { localError, isValidating, validateAndCommit, setLocalError } =
      useFieldValidationState(props.entity.id);

    React.useEffect(() => {
      const timeoutId = window.setTimeout(() => {
        readyForValidationRef.current = true;
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
        readyForValidationRef.current = false;
      };
    }, []);

    const handleChange = React.useCallback(
      async (nextValue: string) => {
        if (!readyForValidationRef.current) {
          props.setValue(nextValue);
          return;
        }

        setLocalError(undefined);
        props.resetError?.();

        await validateAndCommit({
          value: nextValue,
          setValue: props.setValue,
          validator: props.entity.attributes.validator,
          isRequiredInvalid: (value) =>
            Boolean(
              props.entity.attributes.required &&
                parseGridRows(value).length === 0,
            ),
          requiredMessage: "At least one row is required",
          onValidationSuccess: props.resetError,
        });
      },
      [
        props.entity.attributes.required,
        props.entity.attributes.validator,
        props.resetError,
        props.setValue,
        setLocalError,
        validateAndCommit,
      ],
    );

    const entityErrorMessage =
      localError ||
      (props.entity.error instanceof Error
        ? props.entity.error.message
        : formatError(props.entity.value, props.entity.error)?._errors?.[0]);

    return (
      <div className="h-fit">
        <Label htmlFor={id}>
          {!!props.entity.attributes.label.value
            ? props.entity.attributes.label.value
            : props.entity.attributes.name}
        </Label>
        <DataGrid
          value={props.entity.value}
          onChange={(nextValue) => {
            void handleChange(nextValue);
          }}
          headers={props.entity.attributes.gridHeaders as GridHeaderItem[]}
          maxRows={props.entity.attributes.rowConfig?.maxRows}
          showHiddenColumnsPreview={showHiddenColumnsPreview}
          readonly={Boolean(props.entity.attributes.readonly)}
          disabled={Boolean(props.entity.attributes.disabled)}
        />
        {isValidating ? (
          <p className="text-sm mt-1 text-secondary-text">
            Running validation...
          </p>
        ) : entityErrorMessage ? (
          <ValidationError>{entityErrorMessage}</ValidationError>
        ) : null}
      </div>
    );
  },
);
